/**
 * Story 033 — bootstraps the defect-catalogue store on Analysis-page mount.
 *
 * Responsibilities:
 *   1. Fires `useDefectCataloguesQuery` + `useFavouritesQuery` so the
 *      Zustand store is hydrated before downstream consumers (Stories
 *      020 / 023 / 030) need it.
 *   2. Fires `useDefectCatalogueQuery(activeCatalogueId)` when Story 047's
 *      picker sets the active id.
 *   3. Renders the empty-fallback banner (AC6).
 *   4. Mounts a test-bridge under `window.__aeroCatalogues*` so the
 *      Playwright acceptance spec can drive the store without the UI
 *      (Story 020 / 047 haven't shipped yet).
 *
 * No visual output beyond the banner.
 */
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Box } from "@mui/material";
import { useApi } from "@aeriosiq/api-client";
import { useCataloguesStore } from "./cataloguesStore.js";
import {
  catalogueKeys,
  useDefectCataloguesQuery,
  useDefectCatalogueQuery,
  useFavouritesQuery,
  useToggleFavouriteMutation,
} from "./queries.js";
import {
  GLOBAL_DEFAULT_CATALOGUE_ID,
  isUsingGlobalDefault,
} from "./catalogueDefaults.js";
import type { GetDefectCatalogueResponse } from "@aeriosiq/types";

interface TestBridge {
  setActiveCatalogueId: (id: string | null) => void;
  toggleFavourite: (
    catalogueId: string,
    entryId: string,
    favourite: boolean,
  ) => Promise<void>;
  loadEntries: (catalogueId: string) => Promise<void>;
  refetchCatalogues: () => Promise<void>;
  bySearch: (q: string) => unknown[];
}

declare global {
  interface Window {
    __aeroCatalogues?: ReturnType<typeof useCataloguesStore.getState>;
    __aeroCataloguesBridge?: TestBridge;
  }
}

/**
 * Mount once per Analysis page. Safe to render under Suspense — the
 * underlying queries are non-suspending (useQuery, not useSuspenseQuery)
 * so the page never hangs on the catalogues fetch.
 */
export function CataloguesBootstrap(): React.ReactElement {
  const qc = useQueryClient();
  const api = useApi();
  const cataloguesQuery = useDefectCataloguesQuery();
  useFavouritesQuery();
  const activeCatalogueId = useCataloguesStore((s) => s.activeCatalogueId);
  useDefectCatalogueQuery(activeCatalogueId);
  const toggleFavourite = useToggleFavouriteMutation();
  const availableCatalogues = useCataloguesStore((s) => s.availableCatalogues);
  const usingGlobalDefault = isUsingGlobalDefault(availableCatalogues);

  // Test-bridge — exposed in all builds because it's the integration seam
  // for the acceptance spec and there's nothing privacy-sensitive about
  // catalogue ids. The store reference is live (getState()) so the spec
  // always sees the current snapshot.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const w = window;
    Object.defineProperty(w, "__aeroCatalogues", {
      configurable: true,
      get: () => useCataloguesStore.getState(),
    });
    w.__aeroCataloguesBridge = {
      setActiveCatalogueId: (id) => {
        useCataloguesStore.getState().setActiveCatalogueId(id);
      },
      toggleFavourite: async (catalogueId, entryId, favourite) => {
        await toggleFavourite.mutateAsync({ catalogueId, entryId, favourite });
      },
      loadEntries: async (catalogueId) => {
        // Fetch via the axios client directly so the bridge works even
        // before Story 047's picker is wired into the React tree. The
        // store hydration happens here, not in a useQuery effect.
        const res = await api.get<GetDefectCatalogueResponse>(
          `/defect-catalogues/${encodeURIComponent(catalogueId)}`,
        );
        useCataloguesStore
          .getState()
          .setEntries(res.data.catalogueId, res.data.entries);
        // Mirror the data into the query cache so a future
        // `useDefectCatalogueQuery(catalogueId)` doesn't refetch.
        qc.setQueryData(catalogueKeys.detail(catalogueId), res.data);
      },
      refetchCatalogues: async () => {
        await qc.invalidateQueries({ queryKey: catalogueKeys.list() });
        await cataloguesQuery.refetch();
      },
      bySearch: (q) => useCataloguesStore.getState().bySearch(q),
    };
    return () => {
      delete w.__aeroCataloguesBridge;
    };
  }, [qc, api, cataloguesQuery, toggleFavourite]);

  if (!usingGlobalDefault) return <></>;
  return (
    <Box sx={{ mb: 1 }}>
      <Alert
        severity="info"
        role="status"
        aria-label="Using global default catalogue"
        data-testid="catalogues-global-default-banner"
        data-catalogue-id={GLOBAL_DEFAULT_CATALOGUE_ID}
      >
        Using global default catalogue — your admin can create a tenant-specific
        catalogue from the Administration section.
      </Alert>
    </Box>
  );
}
